import React, { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { showNotification } from "../../common/headerSlice"
import api from "../../../utils/axios"
import { triggerRefresh } from "../../../utils/refreshRegistry";

async function deleteTheMessage(messageId, deleteS3File = true, isSpecialMeetingMessage) {
  const apiEndpoint = isSpecialMeetingMessage
      ? `/api/special-meeting-messages/${messageId}`
      : `/api/messages/${messageId}`;
  
  try {
    const deleteMessage = await api.delete(`${apiEndpoint}?deleteS3File=${deleteS3File}`, {
      withCredentials: true,
    });
    return deleteMessage
  } catch (error) {
    console.error("Error deleting message:", error);
    throw error;
  }
}

function DeleteMessageModalBody({closeModal, extraObject }){
  const dispatch = useDispatch()
  const { messageId, isSpecialMeetingMessage } = extraObject;
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [hasS3File, setHasS3File] = useState(false);
  const [showRowOnlyConfirm, setShowRowOnlyConfirm] = useState(false);
  const [showDeleteFileConfirm, setShowDeleteFileConfirm] = useState(false);

  // Fetch message details on mount
  useEffect(() => {
    const fetchMessage = async () => {
      try {
        const apiEndpoint = isSpecialMeetingMessage
          ? `/api/special-meeting-messages/${messageId}`
          : `/api/messages/${messageId}`;

        const response = await api.get(apiEndpoint);
        setMessage(response.data);
        // Check if message has an S3 file that exists
        const hasS3 = response.data?.s3File?.exists === true;
        setHasS3File(hasS3);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching message:", error);
        dispatch(showNotification({ 
          message: "Failed to load message details.", 
          status: 0 
        }));
        setLoading(false);
      }
    };
    
    fetchMessage();
  }, [messageId, dispatch, isSpecialMeetingMessage]);

  const deleteMessage = async (deleteS3File = true) => {
    setDeleting(true);
    setShowRowOnlyConfirm(false);
    setShowDeleteFileConfirm(false);
    try {
      const response = await deleteTheMessage(messageId, deleteS3File, isSpecialMeetingMessage);
      const message = deleteS3File && response.data?.s3FileDeleted
        ? "Message and audio file deleted successfully!"
        : "Message deleted successfully!";
      dispatch(showNotification({ message, status: 1 }));
      closeModal()
      if (isSpecialMeetingMessage) {
        triggerRefresh("specialMessages");
      } else {
        triggerRefresh("messages");
      }
      triggerRefresh("messages");
    } catch (error) {
      console.error("Error deleting message:", error);
      const errorMessage = error.response?.data?.message || "Failed to delete message. Please try again.";
      dispatch(showNotification({ 
        message: errorMessage, 
        status: 0 
      }));
    } finally {
      setDeleting(false);
    }
  }

  const handleDeleteRowOnly = () => {
    setShowRowOnlyConfirm(true);
  }

  const handleDeleteWithFile = () => {
    setShowDeleteFileConfirm(true);
  }

  if (loading) {
    return (
      <>
        <div className="flex justify-center items-center py-8">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </>
    );
  }

  return (
    <>
      {showRowOnlyConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 m-0 p-0" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete Row Only</h3>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-3">
              <p className="text-sm text-red-700">
                <strong>⚠️ Warning:</strong> If you delete the row only, make sure to save the audio file URL and use it somewhere else before deleting the message. Otherwise, the audio file will be unused and waste storage space.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button 
                className="btn btn-ghost" 
                onClick={() => setShowRowOnlyConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="btn btn-error" 
                onClick={() => deleteMessage(false)}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Deleting...
                  </>
                ) : (
                  "Confirm Delete Row Only"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteFileConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 m-0 p-0" style={{ margin: 0, padding: 0 }}>
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete Message & Audio File</h3>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-3">
              <p className="text-sm text-yellow-700">
                <strong>⚠️ Warning:</strong> This will permanently delete the message record and the audio file from S3 storage. This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button 
                className="btn btn-ghost" 
                onClick={() => setShowDeleteFileConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="btn btn-warning" 
                onClick={() => deleteMessage(true)}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Deleting...
                  </>
                ) : (
                  "Confirm Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pb-4">
        {hasS3File && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800 font-semibold">
              ⚠️ This message has an associated audio file in S3 storage
            </p>
          </div>
        )}

        {message && (
          <div className="mt-4 text-sm text-gray-600">
            <p><strong>Title:</strong> {message.title}</p>
            <p><strong>Date:</strong> {message.date ? new Date(message.date).toLocaleDateString() : 'N/A'}</p>
            {hasS3File && (
              <p className="text-xs text-gray-500 mt-2 break-all">
                <strong>Audio File URL:</strong> {message.audioUrl}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="modal-action">
        <button
          className="btn btn-ghost"
          onClick={() => closeModal()}
          disabled={deleting}
        >
          Cancel
        </button>
        
        {hasS3File ? (
          <>
            <button
              className="btn btn-error"
              onClick={handleDeleteRowOnly}
              disabled={deleting || showRowOnlyConfirm || showDeleteFileConfirm}
            >
              Delete Row Only
            </button>
            <button
              className="btn btn-warning"
              onClick={handleDeleteWithFile}
              disabled={deleting || showRowOnlyConfirm || showDeleteFileConfirm}
            >
              Delete Message & Audio File
            </button>
          </>
        ) : (
          <button 
            className="btn btn-error px-6" 
            onClick={() => deleteMessage(true)}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </button>
        )}
      </div>
    </>
  )
}

export default DeleteMessageModalBody
